import {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  HttpStatusCode,
  type InternalAxiosRequestConfig,
} from 'axios';

import {
  type IApiThrowsError,
  type IRejectConfig,
  type IRejectEvent,
  Logger,
  getSessionStorage,
} from '@gbs-monorepo-packages/common';

import { NOT_ALLOWED_METHOD_ADMIN_VIEWING_AS_CLIENT_ERROR } from '../constants';
import { AuthStorageKey } from '../constants/Storage';
import {
  validateAdminViewingMethod,
  validateAdminViewingRoute,
} from '../utils';
import { type IAuthUrls } from './auth';

interface IFailedRequests {
  onSuccess: () => void;
  onFailure: (err: AxiosError) => void;
}

let isRefreshing = false;
const failedRequestsQueue = new Set<Partial<IFailedRequests>>();
export interface IAuthRejectEvent extends IRejectEvent {
  handlerBroadcastSignOut: () => void;
  handlerNotifyForbidden: () => void;
  handlerRefresh: (
    axiosInstance: AxiosInstance,
    customUrls?: Partial<IAuthUrls>
  ) => Promise<void>;
}

export interface IAuthRejectConfig extends IRejectConfig<IAuthRejectEvent> {}

export const handlerRequest = async (
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig<any>> => {
  config.headers.Authorization = `Bearer ${
    localStorage.getItem('JWT_TOKEN') ?? ''
  }`;

  const isAdminViewingAsClient =
    getSessionStorage<boolean>(AuthStorageKey.IS_ADMIN_VIEWING_AS_CLIENT) ??
    false;

  if (isAdminViewingAsClient && config.method) {
    const isAllowedMethod = validateAdminViewingMethod(config.method);
    const isAllowedRoute = validateAdminViewingRoute(config.url ?? '');
    if (!isAllowedMethod && !isAllowedRoute) {
      throw new Error(NOT_ALLOWED_METHOD_ADMIN_VIEWING_AS_CLIENT_ERROR);
    }
  }

  return await Promise.resolve(config);
};

interface IErrorResponseBody {
  message?: string;
  error?: { message?: string };
}

const getErrorDataFromResponse = async (
  data: unknown
): Promise<IErrorResponseBody | null> => {
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      return JSON.parse(text) as IErrorResponseBody;
    } catch {
      return null;
    }
  }
  if (
    data !== null &&
    data !== undefined &&
    typeof data === 'object' &&
    !Array.isArray(data)
  ) {
    return data as IErrorResponseBody;
  }
  return null;
};

export const handlerRejection = async (
  err: IApiThrowsError,
  {
    axiosInstance,
    customUrls,
    baseURL,
    debug = false,
    events,
  }: IAuthRejectConfig
): Promise<AxiosResponse<any, any>> => {
  debug && Logger.debug('interceptors ERROR:', [err, err.response?.status]);
  if (err.config?.baseURL === baseURL) {
    const errorData = await getErrorDataFromResponse(err.response?.data);

    switch (err.response?.status) {
      case HttpStatusCode.Unauthorized:
        if (
          errorData?.message === 'Expired JWT Token' ||
          errorData?.error?.message === 'Expired JWT Token'
        ) {
          return await tryRefreshingToken(
            err,
            axiosInstance,
            events,
            customUrls
          );
        }
        events.handlerBroadcastSignOut();
        break;
      case HttpStatusCode.Forbidden:
        if (
          errorData?.message === 'Forbidden' ||
          errorData?.error?.message === 'Forbidden'
        ) {
          events.handlerNotifyForbidden();
        } else if (
          (errorData?.message?.includes('Access Denied') ?? false) ||
          (errorData?.error?.message?.includes('Access Denied') ?? false)
        ) {
          return await tryRefreshingToken(
            err,
            axiosInstance,
            events,
            customUrls
          );
        }
        break;
    }
  }

  return await Promise.reject(err);
};

const tryRefreshingToken = async (
  err: IApiThrowsError,
  axiosInstance: AxiosInstance,
  events: IAuthRejectEvent,
  customUrls: Record<string, string> | undefined
) => {
  const originalConfig = err.config ?? {};

  const failedRequest: Partial<IFailedRequests> = {};
  // enqueue failed requests
  const promiseFailedRequest = new Promise<AxiosResponse>((resolve, reject) => {
    failedRequest.onSuccess = () => {
      resolve(axiosInstance(originalConfig));
    };
    failedRequest.onFailure = (err: AxiosError) => {
      reject(err);
    };
    failedRequestsQueue.add(failedRequest);
  });

  if (!isRefreshing) {
    isRefreshing = true;
    // refresh token
    try {
      await events.handlerRefresh(axiosInstance, customUrls);
      // make failed requests again
      failedRequestsQueue.forEach((req) => {
        req.onSuccess?.();
      });
    } catch (error) {
      // set requests with failure
      failedRequestsQueue.forEach((req) => {
        req.onFailure?.(err);
      });

      // logout user in all browser tabs
      events.handlerBroadcastSignOut();
    } finally {
      isRefreshing = false;
    }
  }

  const resolvedRequest = await promiseFailedRequest;
  failedRequestsQueue.delete(failedRequest);

  return resolvedRequest;
};
